import { useContext, useEffect, useMemo, useState } from "react";
import BryntumContext from "@/react/context/bryntumContext";
import { SharedModal } from "@/modern/components/shared-modal-component";
import { FormattedFile, useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { Dialog } from "@/modern/components/ui/dialog-shadcn";
import ImageAnnotationEditor from "@/react/components/entityViews/files/components/editors/ImageAnnotationEditor";
import { useImageLoading } from "@/react/components/entityViews/files/hooks/useImageLoading";
import { useFileNavigation } from "@/react/components/entityViews/files/hooks/useFileNavigation";
import { useFileDetailForm } from "@/react/components/entityViews/files/hooks/useFileDetailForm";
import { formatFileMetadata } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { FileDetailHeader } from "@/react/components/entityViews/files/components/FileDetailHeader";
import { FilePreviewArea } from "@/react/components/entityViews/files/components/FilePreviewArea";
import { FileMetadataSection } from "@/react/components/entityViews/files/components/FileMetadataSection";
import { FilePermissionsSection } from "@/react/components/entityViews/files/components/FilePermissionsSection";
import { FileDescriptionSection } from "@/react/components/entityViews/files/components/FileDescriptionSection";
import { EntityRecord } from "@/types/entity";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { useFileSpotIncentive } from "@/react/components/entityViews/files/hooks/useFileSpotIncentive";

interface FileDetailModalProps {
  entityRecord: EntityRecord;
  file: FormattedFile | null;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const FileDetailModal = ({ file, open, setOpen, entityRecord }: FileDetailModalProps) => {
  const { swivuAppGrants, project, editorHandler } = useContext(BryntumContext);
  const { files, handleFileOpen } = useEntityFileContext();
  const [isAnnotating, setIsAnnotating] = useState(false);

  // Check if file is an image to determine if we should load it
  const ImageFile = useMemo(() => (isImageFileTyped(file) ? file : null), [file]);
  const imageUrlToLoad = ImageFile?.originalImageUrl;

  // Custom hooks for business logic
  const { imageLoading, showSpinner, displayedImageUrl, currentImageUrl, handleLoadComplete } = useImageLoading(ImageFile?.id, imageUrlToLoad);
  const { hasPrevious, hasNext, handlePrevious, handleNext } = useFileNavigation(file, files, handleFileOpen);
  const { form, canEditFile, handleSubmit, handleFileDownload } = useFileDetailForm(file, () => setOpen(false));
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler,
    onSuccess: () => setOpen(false)
  });

  // Computed values
  const metadataRows = useMemo(() => formatFileMetadata(file), [file]);
  const shouldShowSpotIncentiveButton = shouldShowAdjustPoints(file);

  // Add keyboard listener for navigation
  // Note: This useEffect is necessary to sync keyboard events with dialog open state
  useEffect(() => {
    if (!open) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (e.key === "ArrowLeft") {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, handlePrevious, handleNext]);

  const handleAnnotating = () => {
    if (file && project && file.originalImageUrl) {
      setIsAnnotating(true);
      setOpen(false);
    }
  };

  const handleCreateSpotIncentive = () => {
    if (file) {
      handleAdjustPoints(file);
    }
  };

  return (
    <>
      {/* Image Annotation Editor */}
      {ImageFile && isAnnotating && project && <ImageAnnotationEditor entityRecord={entityRecord} project={project} fileToAnnotate={ImageFile} onClose={() => setIsAnnotating(false)} />}

      <Dialog open={open} onOpenChange={setOpen}>
        {file ? (
          <SharedModal className="h-[88dvh] w-full max-w-screen-xl md:max-w-screen-2xl">
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex h-full flex-col" key={file.id}>
              <FileDetailHeader
                openAnnotationDialog={handleAnnotating}
                onCreateSpotIncentive={shouldShowSpotIncentiveButton ? handleCreateSpotIncentive : undefined}
                fileName={file.name}
                canView={file.canView}
                canEditFile={canEditFile}
                file={file}
                isDirty={form.formState.isDirty}
                onDownload={handleFileDownload}
              />

              <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-hidden px-6 py-6 lg:flex-row lg:gap-8">
                <FilePreviewArea
                  file={file}
                  imageLoading={imageLoading}
                  showSpinner={showSpinner}
                  displayedImageUrl={displayedImageUrl}
                  currentImageUrl={currentImageUrl}
                  hasPrevious={hasPrevious}
                  hasNext={hasNext}
                  onLoadComplete={handleLoadComplete}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                />

                <aside className="flex w-full flex-col rounded-3xl border border-border/50 bg-card/50 backdrop-blur lg:w-[360px]">
                  <div className="flex-1 space-y-6 overflow-y-auto px-5 py-6">
                    <FileMetadataSection metadataRows={metadataRows} />

                    {!file.isCCImage && <FilePermissionsSection form={form} swivuAppGrants={swivuAppGrants} canEditFile={canEditFile} />}

                    <FileDescriptionSection form={form} canEditFile={canEditFile} isCCImage={file.isCCImage} />
                  </div>
                </aside>
              </div>
            </form>
          </SharedModal>
        ) : null}
      </Dialog>
    </>
  );
};
